import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import React from 'react';
import { removeNavigationListener } from 'app/app.router.navigation-handling';
import {
    processSignLicenseClick as sendAnalyticsOnClick,
    processSignLicenseStepLoad as sendAnalyticsOnLoad,
    pageEvents,
} from 'app/blocks/analytics/analytics';
import { Layout } from 'app/blocks/blocks';
import ASInfo from 'app/blocks/common/as-info';
import { processing, tryCatch } from 'app/blocks/common/decorators';
import showDialog from 'app/blocks/common/jsx/dialogModal';
import PageEnum from 'app/blocks/common/PageEnum';
import * as Utils from 'app/blocks/common/utils';
import { Handlers, setStateAsPromise } from 'app/blocks/common/utils';
import middlewareCPS from 'app/blocks/middleware/cps';
import * as middlewareDashboard from 'app/blocks/middleware/dashboard';
import * as middleware from 'app/blocks/middleware/middleware';
import { isBlocked } from 'app/pages/dashboard/utils';
import withContext from 'app/pages/license-signing-process/context/withContext';
import { OpenAccessOrdersFlags } from 'app/pages/orders/orders.flags';
import flow from 'app/pages/orders/orders.flow';
import { confirmOrderChanges, notifyUserThatOrderIsSaved, shouldLeaveOrdering } from 'app/pages/orders/orders.utils';
import routes from 'app/pages/routes';
import { navigate } from 'app/route/history';
import InitiateController from './initiate/controller';
import errorProcessing from './review/process-error';
import middlewareWPG from '../../../blocks/middleware/wpg';

const Context = React.createContext({});

const OpenAccessOrderStepEnum = {
    INITIATE: 'initiate',
    REVIEW: 'review',
    PAYMENT: 'payment',
    CONFIRMATION: 'confirmation',
};

const defaultBreadcrumbs = Object.values(OpenAccessOrderStepEnum).map(action => ({ action }));

const handlers = new Handlers();
function withProvider(WrappedComponent) {
    @autobind
    class OpenAccessOrderProvider extends React.PureComponent {
        // eslint-disable-next-line react/no-unused-class-component-methods
        _ = handlers.link(this, 'onLoad');

        static propTypes = {
            action: PropTypes.string,
            articleId: PropTypes.string.isRequired,
            error: PropTypes.shape({ code: PropTypes.string, refId: PropTypes.string }),
            loadedOrder: PropTypes.shape({}),
            section: PropTypes.string,
        };

        static defaultProps = {
            action: undefined,
            error: undefined,
            loadedOrder: undefined,
            section: undefined,
        };

        state = {
            agreementRequiredAndNotProvided: true,
            breadcrumbs: defaultBreadcrumbs,
            errors: { initiate: {} },
            isTimeout: false,
            isLoading: true,
            alipayAgreementRequiredAndNotProvided: true,
            ignoredCpsErrors: ['CANCELLED'],
            specificCpsErrors: ['TIMEOUT', 'FAILED'],
        };

        onLoad(error) {
            const { action, article, journal } = this.state;
            const data = { article, journal, step: { name: 'payment', isConfirmed: false, substep: action } };
            sendAnalyticsOnLoad(data, pageEvents.PAGE_LOAD, { error });

            if (error) {
                throw error;
            }
        }

        @tryCatch.showPopUpAndGoToDashboard
        @tryCatch({ errorHandler: e => handlers.onLoad(e) })
        @processing('isLoading')
        async componentDidMount() {
            const { action, correlationId, error, loadedOrder, section, socopayOrderNumber, cpsError } = this.props;
            let { articleId } = this.props;
            if (error) {
                // FIXME can not find the place where correlationId is passed. Is it really passed?
                errorProcessing({
                    error,
                    correlationId,
                    controller: this,
                    returnToInitiate: this.initiate,
                });
            }

            let orderPromise;
            if (loadedOrder) {
                orderPromise = Promise.resolve(loadedOrder);
                articleId = loadedOrder.article.id;
            } else {
                orderPromise = middleware.openaccess.getOrder(articleId, correlationId, socopayOrderNumber);
            }

            const [getArticleResponse, addresses, email, order, socopayEnabled] = await Promise.all([
                middlewareDashboard.getArticle(articleId),
                middleware.profile.getAddresses(),
                middleware.profile.getEmailDetails(),
                orderPromise,
                middleware.payment.getSocopayEnabled(),
            ]);
            const { article, journal, rcaInfo } = getArticleResponse.payload;

            if (isBlocked({ rcaInfo })) {
                navigate('/dashboard');
            }

            await setStateAsPromise.bind(this)({
                article,
                journal,
                order,
                initialOrder: Utils.cloneDeepSimple(order),
                initOrder: { ...order },
                profileAddresses: addresses,
                emailDetails: email,
                action: action || 're-loading',
                cpsError,
                socopayEnabled,
            });

            const props = {
                section,
                article,
                journal,
            };
            const getOrder = () => this.state.order;
            const getSocopayEnabled = () => this.state.socopayEnabled;

            const updateInitiateState = () => {
                this.setState({
                    initiate: { ...this.initiateController.state },
                    order: this.initiateController.state.order,
                });
            };

            this.initiateController = InitiateController(section);
            this.initiateController.init(
                {
                    profileAddresses: this.state.profileAddresses,
                    emailDetails: this.state.emailDetails,
                    onInitiateComplete: this.onInitiateComplete,
                    onSaveForLater: this.onSaveForLater,
                    getOrder,
                    getSocopayEnabled,
                    errors: this.state.errors.initiate,
                    ...props,
                },
                updateInitiateState,
            );

            this.initiateController.handleErrors(this.state.errors.initiate);

            updateInitiateState();

            if (section) {
                this.initiateController.scrollTo(section);
            }

            if (this.state.action === 're-loading') {
                this.setState({ action: action || OpenAccessOrderStepEnum.INITIATE });
            }

            this.onLoad();
        }

        componentDidUpdate(prevProps, prevState) {
            if (!!prevState.action && prevState.action !== this.state.action) {
                this.onLoad();
            }
        }

        @tryCatch.popUpOnly
        @processing('isCanceling')
        async onCancelOrder() {
            const { action, article, order } = this.state;

            if (!(await shouldLeaveOrdering('OPEN_ACCESS', action))) {
                return;
            }

            if (OpenAccessOrdersFlags.isCancellableOnEditState(order)) {
                await middleware.openaccess.cancelOrder(order);
            }

            removeNavigationListener();
            routes.navigateToUrl(`article/${article.id}`);
        }

        @tryCatch.popUpOnly
        @processing('isSavingForLater')
        async onSaveForLater(toDashboard = true) {
            const { article, order } = this.state;

            await middleware.openaccess.saveOrder(order);
            this.setState({ initialOrder: Utils.cloneDeepSimple(order) });
            await notifyUserThatOrderIsSaved('OPEN_ACCESS', OpenAccessOrderStepEnum.INITIATE);

            if (toDashboard) {
                removeNavigationListener();
                routes.navigateToUrl(`article/${article.id}`);
            }
        }

        onInitiateComplete(order) {
            this.onConfirmSubstep();
            this.setState({
                action: 'review',
                order,
            });
        }

        async onReviewComplete(isPaymentRequired) {
            const { order } = this.state;

            this.setState({
                action: isPaymentRequired ? OpenAccessOrderStepEnum.PAYMENT : OpenAccessOrderStepEnum.CONFIRMATION,
            });

            if (isPaymentRequired) {
                try {
                    if (order.paymentDetails.paymentMethod === 'CREDIT_CARD') {
                        await middlewareWPG.getOpenAccessWPGConfigAndSubmitForm(order.correlationId);
                    } else if (order.paymentDetails.paymentMethod === 'SOCOPAY') {
                        await middlewareCPS.getCPSConfigAndSubmitForm(order.chinaPayPricingDetails.socopayOrderNumber);
                    }
                } catch (error) {
                    showDialog.addErrorMessageDialog(error, this.review);
                }
            }
        }

        onRequestEditOrder(type) {
            this.setState({ agreementRequiredAndNotProvided: true });
            this.initiate();
            this.initiateController.showPanel(type);
        }

        onConfirmSubstep(isConfirmed = false) {
            const { action, article, journal } = this.state;
            sendAnalyticsOnClick({ article, journal, step: { name: 'payment', isConfirmed, substep: action } });
        }

        @processing('isReviewConfirming')
        async onReviewConfirm() {
            this.onConfirmSubstep();
            const { correlationId, l } = this.props;
            const { initialOrder, order } = this.state;

            const isOrderChanged = JSON.stringify(initialOrder) !== JSON.stringify(order);

            if (isOrderChanged && order.existing && !(await confirmOrderChanges('OPEN_ACCESS'))) {
                return;
            }

            if (flow.isCreditCardShouldBeValidated(order) || flow.isPaymentMethodAlipay(order)) {
                await this.onReviewComplete(true);
            } else {
                try {
                    await middleware.openaccess.submitOrder(order);
                    await this.onReviewComplete(false);
                } catch (error) {
                    if (error.code?.includes('504')) {
                        this.setState({ isTimeout: true });
                        showDialog.modal(closeDialogAnd => ({
                            type: 'info',
                            message: l('ORDER_PANELS.PANEL_ORDER_SUBMIT_TIMEOUT.MESSAGE'),
                            closeBtnLabel: l('ORDER_PANELS.PANEL_ORDER_SUBMIT_TIMEOUT.BUTTON'),
                            onClose: closeDialogAnd(() => routes.navigateToDashboard()),
                        }));
                    } else {
                        errorProcessing({
                            error,
                            correlationId,
                            onRequestEditOrder: this.onRequestEditOrder,
                        });
                    }
                }
            }
        }

        orderChanged() {
            const { initiate, order, initialOrder, action } = this.state;

            if (!order) {
                return false;
            }

            if (action === OpenAccessOrderStepEnum.INITIATE) {
                const { payment, addresses } = initiate;

                return [
                    [initialOrder, order],
                    [payment.paymentDetails, initialOrder.paymentDetails],
                    [addresses.mainAddress, initialOrder.billingAddress],
                    [addresses.vatTaxDetails, initialOrder.vatTaxDetails],
                ].some(pair => !Utils.looseEqual(pair[0], pair[1]));
            }

            return false;
        }

        initiate() {
            this.setState({ action: OpenAccessOrderStepEnum.INITIATE });
        }

        review() {
            this.setState({ action: OpenAccessOrderStepEnum.REVIEW });
        }

        toggleAgreement() {
            this.setState(state => ({ agreementRequiredAndNotProvided: !state.agreementRequiredAndNotProvided }));
        }

        toggleAgreementAlipay() {
            this.setState(state => ({
                alipayAgreementRequiredAndNotProvided: !state.alipayAgreementRequiredAndNotProvided,
            }));
        }

        render() {
            if (this.state.isProcessing) {
                return <Layout isLoading />;
            }

            const { article, journal } = this.state;

            return (
                <Context.Provider
                    value={{
                        ...this.state,
                        initiateController: this.initiateController,
                        onCancelOrder: this.onCancelOrder,
                        onConfirmSubstep: this.onConfirmSubstep,
                        onReviewConfirm: this.onReviewConfirm,
                        onRequestEditOrder: this.onRequestEditOrder,
                        onSaveForLater: this.onSaveForLater,
                        orderChanged: this.orderChanged,
                        toggleAgreement: this.toggleAgreement,
                        toggleAgreementAlipay: this.toggleAgreementAlipay,
                    }}
                >
                    <WrappedComponent {...this.props} />
                    <ASInfo article={article} journal={journal} page={PageEnum.OPEN_ACCESS + this.state.action} />
                </Context.Provider>
            );
        }
    }

    return OpenAccessOrderProvider;
}

const useOpenAccessOrderFlow = () => React.useContext(Context);

export { useOpenAccessOrderFlow, OpenAccessOrderStepEnum };

export default {
    withProvider: Component => withProvider(Component),
    withContext: withContext(Context),
};
